import React, { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { useToast } from "../hooks/use-toast";
import { Loader2, Send } from "lucide-react";

// NOTE: submissions are mocked to localStorage until backend is connected.
const ConsultationForm = ({ compact = false }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
    consent: false,
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Please fill required fields", description: "Name and email are required." });
      return;
    }
    if (!form.consent) {
      toast({ title: "Consent required", description: "Please agree to receive messages to proceed." });
      return;
    }
    setLoading(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      await axios.post(`${API}/leads`, form);
      toast({
        title: "Request received!",
        description: "One of our advisors will reach out to you shortly.",
      });
      setForm({ name: "", email: "", phone: "", company: "", message: "", consent: false });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: "Please try again, or call us at +1 919-296-3622.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className={compact ? "space-y-4" : "grid sm:grid-cols-2 gap-4"}>
        <div className="space-y-1.5">
          <Label htmlFor="name">Name *</Label>
          <Input id="name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="John Doe" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email *</Label>
          <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@company.com" />
        </div>
      </div>
      <div className={compact ? "space-y-4" : "grid sm:grid-cols-2 gap-4"}>
        <div className="space-y-1.5">
          <Label htmlFor="phone">Phone</Label>
          <Input id="phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+1 555 000 0000" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="company">Company</Label>
          <Input id="company" value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Your company" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="message">Your requirements & a good time to call</Label>
        <Textarea id="message" rows={compact ? 3 : 4} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about your current setup, number of users, and preferred providers..." />
      </div>
      <div className="flex items-start gap-3">
        <Checkbox id="consent" checked={form.consent} onCheckedChange={(v) => update("consent", !!v)} className="mt-1" />
        <Label htmlFor="consent" className="text-sm font-normal text-slate-600 leading-snug">
          By checking this box, you agree to receive SMS messages from Morrisville Communications LLC.
        </Label>
      </div>
      <Button type="submit" disabled={loading} className="brand-gradient text-white rounded-full px-8 h-11 font-semibold w-full sm:w-auto">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Send</>}
      </Button>
      <p className="text-xs text-slate-400">
        This is a no-cost & no-obligation consultation. We will never share your contact information with any of our partners prior to your verbal/written consent.
      </p>
    </form>
  );
};

export default ConsultationForm;
